import React, { useState, useEffect, useContext } from "react";
import api from "../utils/base_url";
import { AuthContext } from "../context/AuthContext";
import toast from "react-hot-toast";

const ProfileUpdateForm = () => {
  const { user, access } = useContext(AuthContext);
  const [initialData, setInitialData] = useState(null);
  const [formData, setFormData] = useState({
    full_name: "",

    door: "",
    street: "",
    area: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",

    dob: "",
    gender: "",
    profile_image: null,
    manual_entry: false,
  });
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (user?.customer_details?.id && access) {
      (async () => {
        try {
          const res = await api.get(`/customerslist/${user.customer_details.id}/`);
          const data = res.data;

          const address = data.data.address || user.customer_details.address || "";
          const parsed = parseAddress(address);

          setFormData({
            full_name: data.data.full_name || "",
            ...parsed,
            dob: data.data.dob || "",
            gender: data.data.gender || "",
            profile_image: null
          });

          setInitialData({
            full_name: data.data.full_name || "",
            ...parsed,
            dob: data.data.dob || "",
            gender: data.data.gender || "",
            profile_image: null
          });

          if (data.data.profile_image) {
            const imgUrl = data.data.profile_image.startsWith("http")
              ? data.data.profile_image
              : `${data.data.profile_image}`;
            setPreview(imgUrl);
          } else if (user.login_method === "google" && user.customer_details.profile_image) {
            setPreview(user.customer_details.profile_image);
          } else if (user.profile_image) {
            setPreview(user.profile_image);
          }
        } catch (err) {
          console.error("Failed to load customer:", err);
        }
      })();
    }
  }, [user, access]);

  const parseAddress = (address = "") => {
    const parts = address.split(",").map(p => p.trim());

    let city = "";
    let state = "";
    let pincode = "";

    parts.forEach(p => {
      // Tamil Nadu - 636015
      const pinMatch = p.match(/(.+)\s*-\s*(\d{6})/);
      if (pinMatch) {
        state = pinMatch[1].trim();
        pincode = pinMatch[2];
      }
    });

    // City is the word before "State - Pincode"
    const pinIndex = parts.findIndex(p => p.includes(pincode));
    if (pinIndex > 0) {
      city = parts[pinIndex - 1];
    }

    return {
      door: parts[0] || "",
      street: parts[1] || "",
      area: parts[2] || "",
      landmark:
        parts.find(p => p.toLowerCase().startsWith("landmark"))?.replace("Landmark:", "").trim() || "",
      city,
      state,
      pincode
    };
  };
  const fetchAddressFromPincode = async (pincode) => {
    if (!/^\d{6}$/.test(pincode)) return;

    try {
      const response = await fetch(
        `https://pincode.scaleupstores.com/api/v1/pincode/lookup/${pincode}/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-API-KEY": "my_secure_api_key_2026",
          },
        }
      );

      const data = await response.json();

      // SUCCESS
      if (data.status) {
        setFormData((prev) => ({
          ...prev,
          city: data.district || "",
          state: data.state || "",
          manual_entry: false,
        }));

        return;
      }

      // NOT FOUND
      toast.error(
        "Pincode not found. Please enter manually."
      );

      setFormData((prev) => ({
        ...prev,
        city: "",
        state: "",
        manual_entry: true,
      }));

    } catch (error) {

      console.error(
        "Pincode API error:",
        error
      );

      toast.error(
        "Pincode service unavailable. Please enter manually."
      );

      setFormData((prev) => ({
        ...prev,
        city: "",
        state: "",
        manual_entry: true,
      }));
    }
  };
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profile_image" && files?.[0]) {
      setFormData({ ...formData, profile_image: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Build full address in Amazon-style format
    const fullAddress = [
      formData.door,
      formData.street,
      formData.area,
      formData.landmark && `Landmark: ${formData.landmark}`,
      `${formData.city}, ${formData.state} - ${formData.pincode}`,
      "India"
    ]
      .filter(Boolean)
      .join(", ");

    const fd = new FormData();

    fd.append("full_name", formData.full_name);
    fd.append("address", fullAddress);   // 🔥 send merged address
    fd.append("dob", formData.dob);
    fd.append("gender", formData.gender);

    if (formData.profile_image) {
      fd.append("profile_image", formData.profile_image);
    }

    try {
      if (user?.customer_details?.id) {
        await api.put(`customerdetails/${user.customer_details.id}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("customerdetails/", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success("Profile saved successfully!");

      const modalEl = document.getElementById("profileModal");
      const modalInstance = window.bootstrap.Modal.getInstance(modalEl);
      modalInstance.hide();

      setTimeout(() => {
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
        document.body.style.removeProperty("overflow");

        const backdrop = document.querySelector(".modal-backdrop");
        if (backdrop) backdrop.remove();
      }, 100);

    } catch (err) {
      console.error(err);
      toast.error("Error saving profile. Please try again.");
    }
  };
  const resetForm = () => {
    if (initialData) {
      setFormData(initialData);

      // restore preview image
      if (user?.customer_details?.profile_image) {
        setPreview(user.customer_details.profile_image);
      } else {
        setPreview(null);
      }
    }
  };


  return (
    <div
      className="modal fade"
      id="profileModal"
      tabIndex="-1"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={handleSubmit} className="profile-form">
            <div className="modal-header">
              <h5 className="modal-title" style={{ color: "#198754", fontWeight: "700" }}>
                Update Profile
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              ></button>
            </div>

            <div className="modal-body">
              <div className="row g-3">
                {/* Full Name */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="fullName"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="Full Name"
                    />
                    <label htmlFor="fullName">Full Name</label>
                  </div>
                </div>

                {/* Address */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="doorNo"
                      name="door"
                      maxLength="20"
                      value={formData.door}
                      onChange={handleChange}
                      placeholder="House / Flat / Door No"
                    />
                    <label htmlFor="House / Flat / Door No">House / Flat / Door No</label>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="street"
                      name="street"
                      maxLength="80"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Street / Road Name"
                    />
                    <label htmlFor="Street / Road Name">Street / Road Name</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="area"
                      name="area"
                      maxLength="80"
                      value={formData.area}
                      onChange={handleChange}
                      placeholder="Area / Locality"
                    />
                    <label htmlFor="Area / Locality">Area / Locality</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="landmark"
                      name="landmark"
                      maxLength="80"
                      value={formData.landmark}
                      onChange={handleChange}
                      placeholder="Landmark"
                    />
                    <label htmlFor="Landmark">Landmark</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="pincode"
                      name="pincode"
                      maxLength="6"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      value={formData.pincode}
                      onChange={(e) => {
                        // 🔥 Remove everything except digits
                        const value = e.target.value.replace(/\D/g, "");

                        setFormData({
                          ...formData,
                          pincode: value,
                          city: "",
                          state: "",
                          manual_entry: false,
                        });

                        if (value.length === 6) {
                          fetchAddressFromPincode(value);
                        }
                      }}
                      placeholder="Pincode"
                    />

                    <label htmlFor="Pincode">Pincode</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City"
                      disabled={!formData.manual_entry}
                    />
                    <label htmlFor="City">City</label>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="text"
                      className="form-control themed-input"
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      placeholder="State"
                      disabled={!formData.manual_entry}
                    />
                    <label htmlFor="State">State</label>
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <input
                      type="date"
                      className="form-control themed-input"
                      id="dob"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      placeholder="Date of Birth"
                    />
                    <label htmlFor="dob">Date of Birth</label>
                  </div>
                </div>

                {/* Gender */}
                <div className="col-md-6">
                  <div className="form-floating">
                    <select
                      className="form-select themed-input"
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    <label htmlFor="gender">Gender</label>
                  </div>
                </div>

                {/* Profile Photo */}
                <div className="col-md-12">
                  <label className="form-label" style={{ color: "#198754", fontWeight: "600" }}>
                    Profile Photo
                  </label>

                  {/* Show upload only if NOT google login */}
                  {user?.login_method !== "google" ? (
                    <input
                      type="file"
                      className="form-control themed-input"
                      name="profile_image"
                      onChange={handleChange}
                    />
                  ) : null}

                  {preview && (
                    <img
                      src={preview}
                      alt="preview"
                      className="mt-2 rounded"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                        borderRadius: "12px",
                        border: "1px solid #f1e6d4",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer justify-content-center">
              <button
                type="button"
                className="btn-themed"
                data-bs-dismiss="modal"
                onClick={resetForm}
              >
                Close
              </button>
              <button type="submit" className="btn-themed">
                Update Profile
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .themed-input {
          border-radius: 10px;
          border: 1px solid #f1e6d4;
          background: #fffaf4;
          color: #000000ff;
          font-size: 1rem;
          transition: border-color 0.3s ease;
          box-shadow: none;
        }
        .themed-input:focus {
          border-color: #198754;
          background: #fff;
          outline: none;
          box-shadow: 0 0 5px rgba(122, 86, 58, 0.4);
        }
        .profile-form .form-floating > label {
          color: #000000ff;
          font-weight: 600;
        }
        .btn-themed {
          background-color: #198754;
          color: #fff;
          border-radius: 25px;
          border: none;
          padding: 8px 26px;
          font-weight: 500;
          transition: background-color 0.3s ease;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(122, 86, 58, 0.12);
        }
        .btn-themed:hover {
          background-color: #198754;
        }
        .btn-themed:focus, .btn-themed:active {
          background-color: #198754;
          outline: none;
          box-shadow: 0 0 7px rgba(122, 86, 58, 0.5);
        }
      `}</style>
    </div>
  );
};

export default ProfileUpdateForm;
